import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNotifier } from "react-headless-notifier";
import {
  Modal,
  Divider,
  Input,
  Label,
  ReadOnly,
  Toggle,
  Select,
  Tabs,
  FormGroup,
  TextArea,
  Button,
  SelectOption,
  NotifierNotification,
} from "@mwi/ui";
import { useForm } from "react-hook-form";
import useErrorHandler from "hooks/useErrorHandler";
import { useGetReasonQuery, useUpdateReasonMutation } from "api";
import ReasonNotification from "api/types/models/reason_notification";

type Inputs = {
  translation: string | undefined;
  notification: ReasonNotification;
};

type EditVocabularyModalTabs = "translation" | "notification";

type EditVocabularyModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const EditReasonVocabularyModal = ({
  isOpen,
  onClose,
}: EditVocabularyModalProps): JSX.Element => {
  const [selectedTab, setSelectedTab] =
    useState<EditVocabularyModalTabs>("translation");

  const { notify } = useNotifier();
  const errorHandler = useErrorHandler();

  const { id } = useParams();

  const {
    data: reason,
    isLoading: loadingReason,
    isError: errorFetchingReason,
  } = useGetReasonQuery(
    {
      reasonUuid: id!,
    },
    { skip: !id },
  );

  const [
    updateReason,
    {
      error: reasonErrors,
      isLoading: updatingReason,
      isSuccess: didUpdateReason,
      isError: errorUpdatingReason,
    },
  ] = useUpdateReasonMutation();

  const {
    control,
    register,
    watch,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState: { errors },
  } = useForm<Inputs>();

  const notificationTypeOptions = [
    { id: "notification", name: "Notification" },
    { id: "emergency", name: "Emergency" },
  ];

  useEffect(() => {
    if (reason) {
      setValue("translation", reason?.translation ?? undefined);
      setValue("notification", {
        is_active: reason?.notification?.is_active ?? false,
        message: reason?.notification?.message ?? "",
        type: reason?.notification?.type ?? undefined,
      });
    }
  }, [reason, id]);

  useEffect(() => {
    if (didUpdateReason) {
      onClose();
      notify(
        <NotifierNotification
          title="Reason Updated"
          message={`${reason?.name} has been updated`}
        />,
      );
    }
  }, [didUpdateReason]);

  useEffect(() => {
    if (errorUpdatingReason) {
      errorHandler(reasonErrors, setError);
    }

    if (errorFetchingReason) {
      onClose();
      notify(
        <NotifierNotification
          type="error"
          title="Unable to edit reason"
          message="We're unable to allow reasons to be edited at the moment. Please try again later."
        />,
      );
    }
  }, [errorUpdatingReason, errorFetchingReason]);

  const handleSave = (data: Inputs) => {
    if (reason) {
      updateReason({
        id: reason.id,
        body: {
          is_active: reason.enabled,
          translation: data.translation,
          notification: data.notification.type ? data.notification : undefined,
        },
      });
    }
  };

  const handleAfterLeave = () => {
    reset();
    setSelectedTab("translation");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      loading={loadingReason}
      className="flex flex-col tablet:!max-w-[776px]"
      afterLeave={handleAfterLeave}
    >
      <Modal.Header title="Edit Appointment Reason" />

      <Modal.Content className="flex flex-col flex-grow">
        {reason && (
          <>
            <Tabs className="gap-x-10">
              <Tabs.Item
                title="Translation"
                isActive={selectedTab === "translation"}
                onClick={() => setSelectedTab("translation")}
              />
              <Tabs.Item
                title="Notification"
                isActive={selectedTab === "notification"}
                onClick={() => setSelectedTab("notification")}
              />
            </Tabs>

            <form
              className="flex flex-col justify-between flex-grow mt-12"
              onSubmit={handleSubmit(handleSave)}
            >
              {selectedTab === "translation" && (
                <div className="mb-[228px]">
                  <ReadOnly
                    heading="Reason name"
                    value={reason.name}
                    className="!mb-3"
                  />

                  <Divider className="mb-9" />

                  <FormGroup error={errors.translation?.message}>
                    <Label className="!text-sm !leading-[21px]">
                      Translation
                    </Label>
                    <Input size="small" {...register("translation")} />
                  </FormGroup>
                </div>
              )}

              {selectedTab === "notification" && (
                <div>
                  <div className="mb-10">
                    <div className="flex items-center gap-6">
                      <span className="text-lg font-semibold leading-[27px]">
                        Set notification
                      </span>
                      <Toggle name="notification.is_active" control={control} />
                    </div>
                  </div>

                  <div className="mb-4">
                    <FormGroup error={errors.notification?.type?.toString()}>
                      <Label className="!text-sm !leading-[21px]">
                        Notification Type
                      </Label>
                      <Select
                        control={control}
                        name="notification.type"
                        placeholder="Select notification type"
                        disabled={!watch("notification.is_active")}
                      >
                        {notificationTypeOptions?.map((notificationType) => (
                          <SelectOption
                            key={notificationType.id}
                            value={notificationType.id}
                          >
                            {notificationType.name}
                          </SelectOption>
                        ))}
                      </Select>
                    </FormGroup>

                    {watch("notification.type") && (
                      <p className="mb-8">
                        {watch("notification.type") === "notification" &&
                          "Enter the message that will be displayed to pet owners. This notification is for information only and will allow pet owners to continue to book an appointment online."}
                        {watch("notification.type") === "emergency" &&
                          "Enter the message that will be displayed to pet owners. The emergency notification will block the pet owner from booking an appointment online and display a prompt to call."}
                      </p>
                    )}
                  </div>

                  <FormGroup error={errors.notification?.message?.toString()}>
                    <Label className="!text-sm leading-[21px]">Message</Label>
                    <TextArea
                      rows={5}
                      disabled={
                        !watch("notification.is_active") ||
                        !watch("notification.type")
                      }
                      {...register("notification.message")}
                    />
                  </FormGroup>
                </div>
              )}

              <Button
                type="submit"
                className="max-w-[153px] mt-4"
                loading={updatingReason}
                disabled={
                  watch("notification.is_active") &&
                  (!watch("notification.type") ||
                    !watch("notification.message"))
                }
              >
                Save
              </Button>
            </form>
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};

export default EditReasonVocabularyModal;
